import React, { useRef, useState } from 'react';
import { UploadCloud, FileText, X } from 'lucide-react';

const ACCEPTED_TYPES = '.pdf,.doc,.docx';
const MAX_SIZE_MB = 10;

// Files are uploaded AS-IS. No client-side or server-side conversion —
// PDF stays PDF, DOCX stays DOCX. Preview is handled separately by
// PolicyDocumentViewer (react-pdf for PDF, mammoth.js for DOCX preview only).
const PolicyUploadDropzone = ({ file, onFileSelect, error }) => {
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateAndSet = (selected) => {
    if (!selected) return;
    if (selected.size > MAX_SIZE_MB * 1024 * 1024) {
      onFileSelect(null, `File exceeds ${MAX_SIZE_MB}MB limit`);
      return;
    }
    onFileSelect(selected, null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSet(e.dataTransfer.files?.[0]);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
          isDragging ? 'border-red-400 bg-red-50' : 'border-gray-300 hover:border-red-300'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPTED_TYPES}
          className="hidden"
          onChange={(e) => validateAndSet(e.target.files?.[0])}
        />
        {!file ? (
          <>
            <UploadCloud className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600">
              Drag & drop a PDF or Word file, or <span className="text-red-600 font-medium">browse</span>
            </p>
            <p className="text-xs text-gray-400 mt-1">.pdf, .doc, .docx — up to {MAX_SIZE_MB}MB</p>
          </>
        ) : (
          <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 text-left">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span className="text-sm text-gray-800 truncate">{file.name}</span>
              <span className="text-xs text-gray-400 flex-shrink-0">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onFileSelect(null, null); }}
              className="text-gray-400 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
};

export default PolicyUploadDropzone;
